import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "../../lib/api";
import { CONTRACT_STATUS } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader } from "../../components/common";
import { Progress } from "../../components/ui/progress";

const REVIEW_STATES = ["pendiente_validacion", "revision_juridica", "revision_negocio", "revision_financiera"];

export default function Revisiones() {
  const navigate = useNavigate();
  const { map } = useUsers();
  const { data: contracts, isLoading } = useQuery({
    queryKey: ["contracts-review"],
    queryFn: async () => {
      const all = await Promise.all(REVIEW_STATES.map((s) => api.get("/legal/contracts", { params: { status: s } })));
      return all.flatMap((r) => r.data);
    },
  });

  return (
    <div data-testid="revisiones-page">
      <PageHeader title="Revisiones" subtitle="Contratos en revisión con sus checklists jurídico, de negocio y financiero. La aprobación exige checklists completos." />
      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-500">Cargando revisiones…</p>
      ) : !contracts?.length ? (
        <EmptyState title="Nada en revisión" description="No hay contratos en fase de revisión ahora mismo." testid="revisiones-empty" />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {contracts.map((c) => {
            const reviews = c.reviews || {};
            const total = Object.values(reviews).reduce((acc, r) => acc + (r.items?.length || 0), 0);
            const done = Object.values(reviews).reduce((acc, r) => acc + (r.items || []).filter((i) => ["ok", "no_aplica"].includes(i.status)).length, 0);
            const nc = Object.values(reviews).reduce((acc, r) => acc + (r.items || []).filter((i) => i.status === "no_conforme").length, 0);
            return (
              <button key={c.id} onClick={() => navigate(`/legal/contratos/${c.id}?tab=revisiones`)} className="ts-card ts-card-hover p-4 text-left" data-testid={`review-card-${c.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="min-w-0 truncate font-medium text-slate-900">{c.title}</p>
                  <Chip map={CONTRACT_STATUS} value={c.status} />
                </div>
                <p className="mt-1 text-xs text-slate-500">{c.contract_number} · Owner: {userName(map, c.contract_owner)} · Legal: {userName(map, c.legal_reviewer)}</p>
                <div className="mt-3 flex items-center gap-2">
                  <Progress value={total ? (done / total) * 100 : 0} className="h-1.5" />
                  <span className="font-mono text-xs text-slate-500">{done}/{total}</span>
                </div>
                <div className="mt-2 flex gap-2 text-xs">
                  <span className="text-emerald-700">{done} conformes</span>
                  {nc > 0 && <span className="font-semibold text-red-600">{nc} no conformes</span>}
                  {total - done - nc > 0 && <span className="text-slate-400">{total - done - nc} pendientes</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
